import React, { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import 'dayjs/locale/ja'
import { wholeContainerStyle, mainColumnStyle, mainContainerStyle } from '@/constants/layout'
import { Button } from '@/components/atoms/button'
import { AccommodationPaging } from '@/components/molecules/accommodation-paging'
import { LoadingFull } from '@/components/molecules/loading-full'
import { EntireSearchPanel } from '@/components/pages/accommodation-management/entire-search/entire-search-panel'
import { Header } from '@/components/organisms/header'
import { SideMenu } from '@/components/organisms/side-menu'
import { List } from './result/list'
import { EntireSearchContentType, EntireSearchContentInitial, convertLabelWithEntireSearch } from '@/models/entire-search'
import { MAX_NUMBER_DOWNLOADS } from '@/constants/accommodation-management'
import { fetchAccommodationManagementCsv, fetchReservation, fetchReservationCustomExportSetting } from '@/apis/aipass'
import { reloadWrapperStyle } from '../list-layout'
import { ReloadButton } from '@/components/atoms/reload-button'
import { useSearchedConditions } from '@/hooks/use-search-condition'
import { Popup } from '@/components/atoms/popup'
import qs from 'qs'
import { SortKeyType } from '@/components/molecules/sortable-table-header'
import { SortKey } from '../list'
import { SearchReservationList } from '@/models/reservation'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useScrollToTop } from '@/hooks/use-scroll-to-top'
import { AccountContext } from '@/contexts/account'
import { ensureCsvExportPermissionOrAlert } from '@/libs/hotel-pms-permission'

export const EntireSearch = () => {
  const params = qs.parse(window.location.search.substring(1))
  const limitPerPage = 50
  const history = useHistory()
  const { t, i18n } = useTranslation()
  const { permissions, permissionsLoaded, permissionsError } = useContext<any>(AccountContext)
  const { errorHandler } = useErrorHandler()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isDropDownSearchPanelOpen, setIsDropDownSearchPanelOpen] = useState<boolean>(true)
  const [isDropDownCsvOpen, setIsDropDownCsvOpen] = useState<boolean>(false)
  const [page, setPage] = useState(Number(params.page) || 1)
  const [listTotal, setListTotal] = useState<number>(0)
  const [reservations, setReservations] = useState<SearchReservationList[]>([])
  const [entireSearchContent, setEntireSearchContent] = useState<EntireSearchContentType>(EntireSearchContentInitial())
  const { SearchedConditionValues } = useSearchedConditions(convertLabelWithEntireSearch())
  const [sortKey, setSortKey] = useState<SortKeyType>(params.sortKey || { key: SortKey.CheckinDate, order: 'asc' })
  const { scrollRef: listScrollRef, scrollToTop: scrollListToTop } = useScrollToTop()

  const doSearch = async (_page: number, _sortKey: SortKeyType): Promise<void> => {
    setIsLoading(true)
    try {
      setPage(_page)
      setSortKey(_sortKey)
      const res = await fetchReservation({ ...entireSearchContent, page: _page, limit: limitPerPage, sortKey: _sortKey })
      setReservations(res.reservations)
      setListTotal(res.total)
      setIsDropDownSearchPanelOpen(false)
      scrollListToTop()
      // 詳細から戻ったときにソートを復元するため、URL にも sortKey を残す
      history.replace({ pathname: '', search: qs.stringify({ ...entireSearchContent, page: _page, sortKey: _sortKey }) })
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const downloadEntireSearchCsv = async () => {
    if (
      !ensureCsvExportPermissionOrAlert(permissions, t, 'customer_csv_export', {
        permissionsLoaded,
        permissionsError,
      })
    ) {
      return
    }
    // Maximum number of downloads
    if (listTotal >= MAX_NUMBER_DOWNLOADS) {
      alert(
        t('You cannot create more than {{num}} items Please press the download button after narrowing down the number of items', {
          num: MAX_NUMBER_DOWNLOADS,
        }),
      )
      return
    }
    try {
      setIsLoading(true)
      const result = await fetchAccommodationManagementCsv(entireSearchContent)
      if (result != null) {
        history.push({ pathname: 'csv-download-history/list' })
      }
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const downloadCustomExport = async () => {
    if (
      !ensureCsvExportPermissionOrAlert(permissions, t, 'customer_csv_export', {
        permissionsLoaded,
        permissionsError,
      })
    ) {
      return
    }
    setIsDropDownCsvOpen(false)
    // Maximum number of downloads
    if (listTotal >= MAX_NUMBER_DOWNLOADS) {
      alert(
        t('You cannot create more than {{num}} items Please press the download button after narrowing down the number of items', {
          num: MAX_NUMBER_DOWNLOADS,
        }),
      )
      return
    }
    try {
      setIsLoading(true)
      await fetchReservationCustomExportSetting()
      history.push('/accommodation-management/custom-export', entireSearchContent)
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!params || !Object.keys(params).length) {
      return
    }
    doSearch(page, sortKey)
  }, [])

  return (
    <div css={wholeContainerStyle}>
      <SideMenu />
      <div className="mainColumn" css={mainColumnStyle}>
        <Header isBack title={t('Accommodation management')} goBack={history.goBack} />
        <div className="mainContainer" css={mainContainerStyle}>
          <div css={searchEntireContainerStyle}>
            <div css={searchEntireHeaderStyle}>
              <div css={leftHeaderStyle}>
                <div css={titleStyle}>{t('Full search')}</div>
                <Button
                  buttonType={4}
                  width={i18n.language === 'en' ? 118 : 82}
                  height={32}
                  fontSize={12}
                  marginLeft={24}
                  onClick={() => setIsDropDownSearchPanelOpen(!isDropDownSearchPanelOpen)}
                >
                  {t('Detailed search')}
                </Button>
                <div css={reloadWrapperStyle}>
                  <ReloadButton onClick={() => doSearch(page, sortKey)} />
                </div>
                <SearchedConditionValues />
                {isDropDownSearchPanelOpen && (
                  <EntireSearchPanel
                    search={() => {
                      doSearch(1, sortKey)
                      setIsDropDownSearchPanelOpen(!isDropDownSearchPanelOpen)
                    }}
                    clearEntireSearchPanel={() => setEntireSearchContent(EntireSearchContentInitial({ isDisabledParams: true }))}
                    entireSearchContent={entireSearchContent}
                    setEntireSearchContent={setEntireSearchContent}
                  />
                )}
              </div>
              <div css={rightHeaderStyle}>
                <Button
                  buttonType={4}
                  width={100}
                  height={32}
                  fontSize={12}
                  paddingLeft={16}
                  paddingRight={16}
                  marginRight={8}
                  icon={require('@/static/images/csv.svg')}
                  onClick={() => setIsDropDownCsvOpen(!isDropDownCsvOpen)}
                >
                  {t('CSV')}
                  <img src={require('@/static/images/arrow_gray.svg')} css={dropDownIcon} />
                </Button>
                <Popup
                  isOpen={isDropDownCsvOpen}
                  onClose={() => setIsDropDownCsvOpen(false)}
                  customStyle={css({ width: 153, left: 0, top: 32 })}
                >
                  <ul>
                    <li css={dropDownContentStyle} onClick={downloadEntireSearchCsv}>
                      <p css={dropDownTitleStyle}>{t('Booking export')}</p>
                    </li>
                    <li css={dropDownContentStyle} onClick={downloadCustomExport}>
                      <p css={dropDownTitleStyle}>{t('Custom export')}</p>
                    </li>
                    <li css={dropDownContentStyle} onClick={() => history.push({ pathname: 'csv-download-history/list' })}>
                      <p css={dropDownTitleStyle}>{t('Csv download history')}</p>
                    </li>
                  </ul>
                </Popup>

                <AccommodationPaging page={page} setPage={_page => doSearch(_page, sortKey)} total={listTotal} limit={limitPerPage} />
              </div>
            </div>
          </div>
          <List
            reservations={reservations}
            sortKey={sortKey}
            listScrollRef={listScrollRef}
            onChangeSortKey={_sortKey => doSearch(1, _sortKey)}
          />
        </div>
      </div>
      <LoadingFull isLoading={isLoading} />
    </div>
  )
}

const searchEntireContainerStyle = css({ borderRadius: '5px', boxShadow: '0px 0px 6px #0000001a', position: 'relative' })

const searchEntireHeaderStyle = css({
  backgroundColor: '#fff',
  padding: '9px 32px 10px 32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderTopLeftRadius: 5,
  borderTopRightRadius: 5,
})

const leftHeaderStyle = css({
  display: 'flex',
  alignItems: 'center',
})

const rightHeaderStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flex: '0 0 auto',
  position: 'relative',
})

const dropDownIcon = css({
  marginLeft: 8,
})
const titleStyle = css({
  backgroundColor: '#fff',
  fontSize: '18px',
  fontWeight: 'bold',
  color: '#272727',
})

const dropDownContentStyle = css({
  display: 'flex',
  height: 34,
  listStyle: 'none',
  alignItems: 'center',
  padding: '0 16px',
  cursor: 'pointer',
  '&:hover': {
    background: '#f2f2f2',
  },
})

const dropDownTitleStyle = css({
  fontSize: 14,
  color: '#272727',
})
